<template>
    <div class="videoTimelineWrap">
        <div class="vt-expand-handle" @click="$emit('toggle-expand')">
            <Tooltip :text="expanded ? $t('dash.post-creator.media-editor.timeline.collapse') : $t('dash.post-creator.media-editor.timeline.expand')">
                <i class="fa-solid" :class="expanded ? 'fa-caret-down' : 'fa-caret-up'"></i>
            </Tooltip>
        </div>

        <div class="videoTimeline" :class="{ compact: compact && !expanded, expanded }">
            <!-- Three groups in one row, CapCut-style: the clip tools pulled out of the video
                 toolbox on the left (they act on whatever clip is selected, so having them right
                 above the timeline is more direct than digging through the toolbox), play/timecode
                 isolated in the center (never mixed with the scrub control itself - that lives in
                 the ruler below, see vt-ruler/vt-playhead), zoom on the right. -->
            <div class="vt-actions">
                <div class="vt-actions-group vt-actions-tools">
                    <Tooltip
                        v-for="tool in clipTools"
                        :key="tool.type"
                        :text="$t(tool.tooltipKey)"
                        :classCustom="'vt-action-btn' + (tool.type === activeClipTool ? ' active' : '')"
                        @click="onClipToolClick(tool)"
                    ><i :class="tool.icon"></i></Tooltip>
                </div>

                <div class="vt-actions-group vt-actions-center">
                    <Tooltip
                        :text="playing ? $t('dash.post-creator.media-editor.timeline.pause') : $t('dash.post-creator.media-editor.timeline.play')"
                        classCustom="vt-play-btn"
                        @click="$emit('toggle-play')"
                    ><i class="fa-solid" :class="playing ? 'fa-pause' : 'fa-play'"></i></Tooltip>

                    <span class="vt-time">
                        <span class="vt-time-current">{{ formatTimecode(currentMs) }}</span>
                        <span class="vt-time-sep">|</span>
                        <span class="vt-time-total">{{ formatTimecode(totalDurationMs) }}</span>
                    </span>
                </div>

                <div class="vt-actions-group vt-actions-right">
                    <Tooltip
                        :text="hoverPreview ? $t('dash.post-creator.media-editor.timeline.hover-preview-on') : $t('dash.post-creator.media-editor.timeline.hover-preview-off')"
                        :classCustom="'vt-action-btn' + (hoverPreview ? ' active' : '')"
                        @click="hoverPreview = !hoverPreview"
                    ><i class="fa-regular fa-eye"></i></Tooltip>

                    <div class="vt-zoom-controls">
                        <Tooltip
                            :text="$t('dash.post-creator.media-editor.timeline.zoom-out')"
                            :active="canZoomOut"
                            :classCustom="'vt-zoom-btn' + (!canZoomOut ? ' disabled' : '')"
                            @click="zoomOut"
                        ><i class="fa-solid fa-magnifying-glass-minus"></i></Tooltip>
                        <input
                            class="vt-zoom-slider"
                            type="range"
                            min="1"
                            :max="MAX_ZOOM_FACTOR"
                            step="0.1"
                            :value="zoomFactor"
                            @input="e => zoomFactor = Number(e.target.value)"
                        >
                        <Tooltip
                            :text="$t('dash.post-creator.media-editor.timeline.zoom-in')"
                            :active="canZoomIn"
                            :classCustom="'vt-zoom-btn' + (!canZoomIn ? ' disabled' : '')"
                            @click="zoomIn"
                        ><i class="fa-solid fa-magnifying-glass-plus"></i></Tooltip>
                    </div>

                    <Tooltip
                        :text="$t('dash.post-creator.media-editor.timeline.fullscreen')"
                        classCustom="vt-action-btn"
                        @click="$emit('toggle-fullscreen')"
                    ><i class="fa-solid fa-expand"></i></Tooltip>
                </div>
            </div>

            <div class="vt-scroll-area" ref="scrollArea" @wheel="onScrollAreaWheel">
                <div
                    class="vt-timeline-body"
                    :style="{ width: totalWidthPx + 'px' }"
                    @mousemove="onTimelineHover"
                    @mouseleave="onTimelineHoverEnd"
                >
                    <!-- The actual scrub control - CapCut has the ruler (with timestamps) be the
                         drag target, and a single playhead line/cap that visually runs down through
                         every track below it so its position relative to any clip/overlay is always
                         obvious at a glance, instead of a plain <input type=range> that only ever
                         related to its own thin strip. -->
                    <div class="vt-ruler" @mousedown="startScrub">
                        <span
                            v-for="tick in rulerTicks"
                            :key="tick.ms"
                            class="vt-ruler-tick"
                            :style="{ left: tick.leftPx + 'px' }"
                        >{{ tick.label }}</span>
                    </div>

                    <div class="vt-playhead" :style="{ left: playheadLeftPx + 'px' }">
                        <div class="vt-playhead-cap"></div>
                    </div>

                    <!-- Thin precision indicator for the hover-preview mode - separate from the
                         real playhead above, so it's clear this is just where the mouse is
                         hovering (previewing that frame) rather than the actual current position. -->
                    <div v-if="hoverPreview && hoverMs !== null" class="vt-hover-line" :style="{ left: (hoverMs / (totalDurationMs || 1) * contentWidthPx) + 'px' }"></div>

                    <div class="vt-strip-wrapper">
                        <!-- No `filter` option here (tried ".tc-handle" - verified via an isolated
                             repro with the exact same Vue2/vuedraggable/Sortable versions that it
                             silently swallows the mousedown entirely, matching real drag-and-drop
                             semantics browsers apply once Sortable's own gesture handling engages -
                             not the mouse-event conflict it was meant to fix). Our own
                             @mousedown.stop on the handles already keeps Sortable from treating
                             them as a reorder-drag target on its own. -->
                        <draggable v-model="clips" tag="div" class="vt-strip" :animation="150" @end="onDragEnd">
                            <TimelineClip
                                v-for="(clip, index) in clips"
                                :key="clip.id"
                                :clip="clip"
                                :src="sources[clip.fileId] || ''"
                                :selected="clip.id === selectedClipId"
                                :isLast="index === clips.length - 1"
                                :pxPerSec="pxPerSec"
                                :previewSegments="silencePreview && silencePreview.clipId === clip.id ? silencePreview.segments : null"
                                @select="$emit('select-clip', clip.id)"
                                @remove="$emit('remove-clip', clip.id)"
                                @select-clip-tool="$emit('select-clip-tool', 'transition')"
                                @update-trim="patch => $emit('update-clip', { clipId: clip.id, patch })"
                                @toggle-preview-segment="patch => $emit('toggle-silence-segment', patch)"
                                @move-preview-boundary="patch => $emit('move-silence-boundary', patch)"
                                @insert-file-after="startInsertFile(index + 1)"
                                @insert-blank-after="color => $emit('add-blank-image', { index: index + 1, color })"
                            />
                        </draggable>

                        <div class="vt-add-clip">
                            <input ref="fileInput" type="file" accept="video/*,image/*" multiple hidden @change="onFilesPicked">
                            <AddClipMenu @pick-file="startInsertFile()" @pick-blank="color => $emit('add-blank-image', { index: undefined, color })" />
                        </div>
                    </div>

                    <!-- Explicit content width (not a plain 100%, which would stretch to
                         .vt-timeline-body's own totalWidthPx - inflated to never look narrower
                         than the container, see totalWidthPx's comment) - a row here uses percent-
                         of-totalDurationMs positioning internally, which only lines up with the
                         clip strip's own pixel positions above if "100%" means the same thing in
                         both places. Left uncorrected, a track running the whole video's length
                         visibly overran the actual clips into the empty gap .vt-add-clip's button
                         sits in, at fit zoom. -->
                    <div class="ot-tracks-ruler" v-if="overlays.length" :style="{ width: contentWidthPx + 'px' }">
                        <div class="ot-track-lane" v-for="(row, rowIndex) in overlayRows" :key="rowIndex">
                            <OverlayTrackRow
                                v-for="overlay in row"
                                :key="overlay.id"
                                :overlay="overlay"
                                :totalDurationMs="totalDurationMs || 1"
                                :selected="overlay.id === selectedOverlayId"
                                @select="$emit('select-overlay', overlay.id)"
                                @remove="$emit('remove-overlay', overlay.id)"
                                @duplicate="$emit('duplicate-overlay', overlay.id)"
                                @update="patch => $emit('update-overlay', { overlayId: overlay.id, patch })"
                                @drag-start="onOverlayDragStart(overlay.id)"
                                @drag-end="onOverlayDragEnd"
                            />
                        </div>
                    </div>

                    <!-- Background music tracks - each always gets its own lane (unlike overlays,
                         several can deliberately overlap in time, so packing them into shared rows
                         the way overlayRows does isn't useful here). Trimming/repositioning now
                         happens by dragging directly here instead of a slider in MusicPanel, same
                         move as the video clips' own drag-to-trim on the strip above. -->
                    <div class="at-tracks-ruler" v-if="audioClips.length" :style="{ width: contentWidthPx + 'px' }">
                        <div class="at-track-lane" v-for="clip in audioClips" :key="clip.id">
                            <AudioTrackRow
                                :clip="clip"
                                :totalDurationMs="totalDurationMs || 1"
                                :label="fileNames[clip.fileId] || $t('dash.post-creator.media-editor.music-panel.fallback-name')"
                                @select="$emit('select-clip-tool', 'music')"
                                @update="patch => $emit('update-audio-clip', { clipId: clip.id, patch })"
                                @remove="$emit('remove-audio-clip', clip.id)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import TimelineClip from './TimelineClip.vue'
import OverlayTrackRow from './OverlayTrackRow.vue'
import AudioTrackRow from './AudioTrackRow.vue'
import AddClipMenu from './AddClipMenu.vue'
import Tooltip from '@/components/dash/tooltip/tooltipInit.vue'
import { getMediaTrack } from '../engine/timelineModel.js'
import { formatTimecode } from '../engine/time.js'

// zoomFactor is a multiplier over "fit" (the pixels-per-second that makes the whole timeline
// exactly fill the visible area) rather than an absolute px/sec value - that way "2x" always
// means the same relative density regardless of how long the video is or how wide the popup
// happens to be, instead of a fixed px/sec that would look wildly different across videos.
const MAX_ZOOM_FACTOR = 12
const ZOOM_STEP = 1.4
// The single add-clip button (see AddClipMenu.vue, 56px) plus the .vt-strip-wrapper gap next to it
// (8px) sit right after the clips in the same flex row - without reserving their *combined* width
// up front, "fit" zoom sized clips to fill the full row width on their own, leaving no room left
// for the gap and pushing the button 8px past what .vt-strip-wrapper's flex row could actually
// fit, which visibly spilled the button onto the last clip's own thumbnail instead of sitting
// cleanly after it.
const ADD_CLIP_RESERVED_PX = 64

// "Nice" tick intervals to choose from depending on zoom level (seconds) - the largest one whose
// on-screen spacing (interval * pxPerSec) is still comfortably readable wins, so labels never
// crowd together when zoomed out nor get wastefully sparse when zoomed in.
const RULER_TICK_CANDIDATES_SEC = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800]
const RULER_MIN_TICK_SPACING_PX = 70

export default {
    name: 'Timeline',

    components: {
        draggable,
        TimelineClip,
        OverlayTrackRow,
        AudioTrackRow,
        AddClipMenu,
        Tooltip
    },

    watch: {
        hoverPreview(enabled) {
            if (!enabled) {
                this.hoverMs = null
                this.$emit('preview-seek', null)
            }
        },

        // Closed is a hard no-scroll view (see timeline.scss's own overflow:hidden there) - a
        // zoom level left over from before closing would make the ruler itself wider than the
        // visible area with no way to reach the rest of it, so this resets back to fit (1x)
        // every time the timeline closes rather than leaving whatever zoom was last set.
        expanded(isExpanded) {
            if (!isExpanded) this.zoomFactor = 1
        }
    },

    data() {
        return {
            // The overlay currently being repositioned/resized (if any), and which row it was on
            // right before the drag started - see overlayRows below.
            draggingOverlayId: null,
            draggingOverlayRowIndex: 0,
            zoomFactor: 1,
            containerWidthPx: 0,
            scrubbing: false,
            hoverPreview: false,
            hoverMs: null,
            pendingInsertIndex: null,
            MAX_ZOOM_FACTOR,
            // Clip-level actions pulled out of the video toolbox (OverlayPalette) so they sit
            // directly above the timeline they act on - matching CapCut, where these live right
            // over the tracks instead of behind a separate toolbox category.
            clipTools: [
                { type: 'crop', icon: 'fa-solid fa-crop', tooltipKey: 'dash.post-creator.media-editor.palette.tool-crop-tooltip' },
                { type: 'rotate', icon: 'fa-solid fa-rotate', tooltipKey: 'dash.post-creator.media-editor.palette.tool-rotate-tooltip' },
                { type: 'split', action: 'split', icon: 'fa-solid fa-scissors', tooltipKey: 'dash.post-creator.media-editor.palette.tool-split-tooltip' },
                { type: 'silence', icon: 'fa-solid fa-wand-magic-sparkles', tooltipKey: 'dash.post-creator.media-editor.clip-tool.remove-silences' },
                { type: 'speed', icon: 'fa-solid fa-gauge-high', tooltipKey: 'dash.post-creator.media-editor.palette.tool-speed-tooltip' },
                { type: 'background', icon: 'fa-solid fa-fill-drip', tooltipKey: 'dash.post-creator.media-editor.palette.tool-background-tooltip' },
                { type: 'sound', icon: 'fa-solid fa-volume-high', tooltipKey: 'dash.post-creator.media-editor.palette.tool-sound-tooltip' }
            ]
        }
    },

    props: {
        timeline: {
            type: Object,
            required: true
        },
        sources: {
            type: Object,
            default: () => ({})
        },
        // { clipId, segments } while a silence-removal review is active (see MediaEditor.vue) -
        // null otherwise. Only the matching clip actually receives it (see previewSegments below).
        silencePreview: {
            type: Object,
            default: null
        },
        selectedClipId: {
            type: String,
            default: null
        },
        overlays: {
            type: Array,
            default: () => []
        },
        selectedOverlayId: {
            type: String,
            default: null
        },
        audioClips: {
            type: Array,
            default: () => []
        },
        fileNames: {
            type: Object,
            default: () => ({})
        },
        currentMs: {
            type: Number,
            default: 0
        },
        totalDurationMs: {
            type: Number,
            default: 0
        },
        compact: {
            type: Boolean,
            default: false
        },
        expanded: {
            type: Boolean,
            default: false
        },
        playing: {
            type: Boolean,
            default: false
        },
        activeClipTool: {
            type: String,
            default: null
        }
    },

    computed: {
        // Position of the playhead line in the same pixel space as the clips/overlays (not a
        // percentage of the visible viewport) - it needs to line up with them even when the
        // timeline is scrolled/zoomed past what's currently visible. contentWidthPx, not
        // totalWidthPx (see contentWidthPx's own comment) - using the latter here would let the
        // playhead run past the last clip's own right edge at fit zoom, into the gap
        // totalWidthPx pads in to keep the body from looking too narrow on a short video.
        playheadLeftPx() {
            return (this.currentMs / (this.totalDurationMs || 1)) * this.contentWidthPx
        },

        // The px/sec that makes the whole timeline exactly fill the visible scroll area - the
        // baseline "1x" zoom level.
        fitPxPerSec() {
            const totalSec = Math.max(1, this.totalDurationMs / 1000)
            const available = Math.max(0, this.containerWidthPx - ADD_CLIP_RESERVED_PX)
            return available / totalSec
        },

        pxPerSec() {
            return this.fitPxPerSec * this.zoomFactor
        },

        // Never narrower than the visible area (a short/just-started video shouldn't leave the
        // strip looking broken-small) - only grows past it once zoomed in far enough that clips
        // genuinely need more room than the popup can show at once, which is what makes the area
        // scrollable. Only ever used for .vt-timeline-body's own explicit CSS width (so it, and
        // the scrollable area around it, never look narrower than the popup itself) - anything
        // that positions/sizes content *within* that body (playhead, hover-line, overlay/audio
        // rows) needs contentWidthPx instead, or it'd run past the clip strip's own actual right
        // edge into the padding this adds, right where .vt-add-clip's button sits.
        totalWidthPx() {
            return Math.max(this.containerWidthPx, this.contentWidthPx)
        },

        // The clip strip's own natural width - exactly what totalDurationMs of clips actually
        // occupy at the current zoom, with none of totalWidthPx's "never narrower than the
        // container" padding.
        contentWidthPx() {
            return (this.totalDurationMs / 1000) * this.pxPerSec
        },

        // Time-marked ticks along the ruler (00:00, 00:10, 00:20...) - the interval adapts to the
        // current zoom level so labels stay legibly spaced instead of a fixed step that would
        // either crowd together when zoomed out or leave the ruler mostly blank when zoomed in.
        rulerTicks() {
            const intervalSec = RULER_TICK_CANDIDATES_SEC.find(s => s * this.pxPerSec >= RULER_MIN_TICK_SPACING_PX)
                || RULER_TICK_CANDIDATES_SEC[RULER_TICK_CANDIDATES_SEC.length - 1]
            const totalSec = this.totalDurationMs / 1000
            const ticks = []
            for (let s = 0; s <= totalSec + 0.001; s += intervalSec) {
                ticks.push({ ms: Math.round(s * 1000), leftPx: s * this.pxPerSec, label: formatTimecode(s * 1000) })
            }
            return ticks
        },

        canZoomOut() {
            return this.zoomFactor > 1.001
        },

        canZoomIn() {
            return this.zoomFactor < MAX_ZOOM_FACTOR - 0.001
        },

        // Packs overlays into the fewest rows possible, greedily: two overlays only share a row
        // if their time windows never overlap - like a calendar view, instead of always spending
        // one full row per element regardless of whether anything actually collides with it.
        overlayRows() {
            if (!this.draggingOverlayId) return this.packOverlayRows(this.overlays)

            const dragged = this.overlays.find(o => o.id === this.draggingOverlayId)
            if (!dragged) return this.packOverlayRows(this.overlays)

            // While a drag is active, the element being moved gets an exclusive row of its own -
            // at the row index it already occupied, not always the top one, otherwise starting a
            // drag on anything but the very first row instantly jumped it up before the mouse
            // even moved - and every other element is packed *without it*, so repacking the rest
            // around its live, still-changing position can't bump it to a different lane mid-drag
            // either. The full optimized packing (everyone included) only resumes once released.
            const others = this.overlays.filter(o => o.id !== this.draggingOverlayId)
            const otherRows = this.packOverlayRows(others)
            const insertAt = Math.max(0, Math.min(this.draggingOverlayRowIndex, otherRows.length))
            const rows = otherRows.slice()
            rows.splice(insertAt, 0, [dragged])
            return rows
        },

        clips: {
            get() {
                const track = getMediaTrack(this.timeline)
                return track ? track.clips : []
            },
            set(newClips) {
                this.$emit('reorder-clips', newClips)
            }
        }
    },

    mounted() {
        this.containerWidthPx = this.$refs.scrollArea.getBoundingClientRect().width
        this.resizeObserver = new ResizeObserver(() => {
            this.containerWidthPx = this.$refs.scrollArea.getBoundingClientRect().width
        })
        this.resizeObserver.observe(this.$refs.scrollArea)
    },

    beforeDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect()
        this.stopScrub()
    },

    methods: {
        formatTimecode,

        // No selectedClipId guard here - onSelectClipTool/splitSelectedClip (MediaEditor.vue)
        // already fall back to whichever clip sits under the playhead when nothing is explicitly
        // selected, exactly like these tools worked before moving out of the video toolbox.
        onClipToolClick(tool) {
            if (tool.action === 'split') this.$emit('split-clip')
            else this.$emit('select-clip-tool', tool.type)
        },

        // Dragging directly on the ruler (the CapCut-style scrub control - see the template):
        // starts on its own mousedown, but tracks mousemove/mouseup on the window so the drag
        // keeps following the cursor even once it leaves the ruler's own (fairly short) strip.
        startScrub(e) {
            this.scrubbing = true
            this.updateScrubFromEvent(e)
            window.addEventListener('mousemove', this.onScrubMove)
            window.addEventListener('mouseup', this.stopScrub)
        },

        onScrubMove(e) {
            if (!this.scrubbing) return
            this.updateScrubFromEvent(e)
        },

        stopScrub() {
            this.scrubbing = false
            window.removeEventListener('mousemove', this.onScrubMove)
            window.removeEventListener('mouseup', this.stopScrub)
        },

        msFromEvent(e) {
            const rect = this.$refs.scrollArea.getBoundingClientRect()
            const xPx = e.clientX - rect.left + this.$refs.scrollArea.scrollLeft
            return Math.max(0, Math.min(this.totalDurationMs, (xPx / this.pxPerSec) * 1000))
        },

        updateScrubFromEvent(e) {
            this.$emit('seek', Math.round(this.msFromEvent(e)))
        },

        // "Axe de prévisualisation" - while enabled (and not actually dragging the playhead,
        // which already seeks for real), hovering anywhere over the timeline previews that
        // instant's frame on the video canvas without touching the real current position/
        // timecode - mouseleave (or toggling the option off) restores the real frame. See
        // MediaEditor.onPreviewSeek for why this is a no-op while actually playing.
        onTimelineHover(e) {
            if (!this.hoverPreview || this.scrubbing) return
            const ms = Math.round(this.msFromEvent(e))
            this.hoverMs = ms
            this.$emit('preview-seek', ms)
        },

        onTimelineHoverEnd() {
            this.hoverMs = null
            if (!this.hoverPreview) return
            this.$emit('preview-seek', null)
        },

        zoomIn() {
            if (!this.canZoomIn) return
            this.zoomFactor = Math.min(MAX_ZOOM_FACTOR, this.zoomFactor * ZOOM_STEP)
        },

        zoomOut() {
            if (!this.canZoomOut) return
            this.zoomFactor = Math.max(1, this.zoomFactor / ZOOM_STEP)
        },

        // Ctrl+scroll zoom, CapCut/Premiere-style - only hijacks the wheel when ctrlKey is held
        // (also true for a trackpad pinch gesture in Chrome/Firefox) so a plain scroll still lets
        // the browser/container handle it normally instead of always zooming the timeline.
        onScrollAreaWheel(e) {
            if (!e.ctrlKey) return
            e.preventDefault()
            if (e.deltaY < 0) this.zoomOut()
            else if (e.deltaY > 0) this.zoomIn()
        },

        packOverlayRows(overlays) {
            const sorted = [...overlays].sort((a, b) => a.startMs - b.startMs)
            const rows = []
            sorted.forEach((overlay) => {
                const row = rows.find(r => r.lastEnd <= overlay.startMs)
                if (row) {
                    row.items.push(overlay)
                    row.lastEnd = overlay.endMs
                } else {
                    rows.push({ lastEnd: overlay.endMs, items: [overlay] })
                }
            })
            return rows.map(r => r.items)
        },

        onDragEnd() {
            // v-model setter already emitted the reordered array via reorder-clips.
        },

        // Captured from the *current* (still non-isolated) packing, before draggingOverlayId is
        // set - otherwise overlayRows would already have isolated the element by the time we ask
        // where it was, always answering "row 0".
        onOverlayDragStart(overlayId) {
            this.draggingOverlayRowIndex = this.overlayRows.findIndex(row => row.some(o => o.id === overlayId))
            if (this.draggingOverlayRowIndex === -1) this.draggingOverlayRowIndex = 0
            this.draggingOverlayId = overlayId
        },

        onOverlayDragEnd() {
            this.draggingOverlayId = null
        },

        // Shared by the "append at the end" button and every clip's own hover-revealed "insert
        // after me" control (see the TimelineClip listeners above) - both just need to remember
        // where the picked file(s) should land before opening the same file dialog.
        startInsertFile(index) {
            this.pendingInsertIndex = index === undefined ? null : index
            this.$refs.fileInput.click()
        },

        onFilesPicked(e) {
            const files = Array.from(e.target.files || [])
            e.target.value = null
            let index = this.pendingInsertIndex
            files.forEach((file) => {
                this.$emit('add-clip', { file, index: index === null ? undefined : index })
                if (index !== null) index += 1
            })
            this.pendingInsertIndex = null
        }
    }
}
</script>

<style lang="scss" scoped></style>
